import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { courses } from '@/api/courses';
import type { CourseRow, CreateCourseBody } from '@/api/courses';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus, MoreHorizontal, Trash2, GraduationCap, Search, Users as UsersIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function CoursesList() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canMutate = CAN_MUTATE.includes(user.role);
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CourseRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['courses', search, effectiveCustomerId],
    queryFn: () => courses.list({
      search: search.trim() || undefined,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => courses.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['courses'] }); setDeleteTarget(null); },
  });

  return (
    <>
      <PageHeader
        eyebrow="Virtual school"
        title="Courses"
        description="Tele-courses callers enroll in from an IVR flow or manual add. Each course is a syllabus of folders, conferences, quizzes, and surveys."
        actions={canMutate && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New course
          </Button>
        )}
      />

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Search courses…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={canMutate ? () => setCreateOpen(true) : undefined} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((c) => (
            <CourseCard
              key={c.id} row={c}
              canMutate={canMutate}
              onDelete={() => setDeleteTarget(c)}
            />
          ))}
        </div>
      )}

      {canMutate && <CreateCourseDialog open={createOpen} onOpenChange={setCreateOpen} />}

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete course?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{deleteTarget?.title}</span> will be soft-deleted.
            Existing enrollments stay but no new callers can enroll.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive" size="sm"
              disabled={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmptyState({ onCreate }: { onCreate?: () => void }) {
  return (
    <Card>
      <CardContent className="py-16 text-center">
        <GraduationCap className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No courses yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
          Create a course, add sections (folders / conferences / quizzes / surveys),
          then enroll callers via an IVR <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">enrollment</code> node.
        </p>
        {onCreate && (
          <Button size="sm" className="mt-4" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            Create your first course
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function CourseCard({
  row, canMutate, onDelete,
}: {
  row: CourseRow; canMutate: boolean; onDelete: () => void;
}) {
  const capLabel = row.max_enrollment
    ? `${row.enrollment_count}/${row.max_enrollment}`
    : `${row.enrollment_count}`;
  const capPct = row.max_enrollment
    ? Math.min(100, (row.enrollment_count / row.max_enrollment) * 100)
    : 0;

  return (
    <article className="group relative rounded-lg border border-border bg-white hover:shadow-md transition-all overflow-hidden">
      {/* Top stripe */}
      <div className={cn(
        'h-1',
        row.active ? 'bg-primary/80' : 'bg-slate-200',
      )} />
      <Link to={`/courses/CRS-${row.ref_number}`} className="block p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
              {formatRef(row.ref_number, 'CRS')}
            </div>
            <h3 className="text-base font-semibold text-foreground leading-tight truncate">
              {row.title}
            </h3>
          </div>
          {canMutate && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon" variant="ghost"
                  className="h-7 w-7 -mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.preventDefault()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); onDelete(); }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {row.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-4">
            {row.description}
          </p>
        )}

        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sections</div>
            <div className="font-mono tabular-nums font-semibold text-sm mt-0.5">
              {row.section_count}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Price</div>
            <div className="font-mono tabular-nums font-semibold text-sm mt-0.5">
              {row.price_cents > 0
                ? `${row.currency} ${(row.price_cents / 100).toFixed(2)}`
                : 'Free'}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              <UsersIcon className="h-2.5 w-2.5" />
              Enrolled
            </div>
            <div className="font-mono tabular-nums font-semibold text-sm mt-0.5">
              {capLabel}
            </div>
            {row.max_enrollment && (
              <div className="relative mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={cn(
                    'absolute inset-y-0 left-0 transition-all',
                    capPct >= 90 ? 'bg-rose-500' : capPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500',
                  )}
                  style={{ width: `${capPct}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {!row.active && (
          <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-400 text-center">
            Inactive
          </div>
        )}
      </Link>
    </article>
  );
}

function CreateCourseDialog({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('0');
  const [cap, setCap] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateCourseBody) => courses.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courses'] });
      setTitle(''); setDescription(''); setPrice('0'); setCap('');
      setCustomerId(null); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  const priceDollars = Number(price);
  const priceCents = Number.isFinite(priceDollars)
    ? Math.round(priceDollars * 100)
    : 0;
  const capInt = cap.trim() === '' ? null : Number(cap);
  const capValid = cap.trim() === '' || (Number.isFinite(capInt) && Number.isInteger(capInt) && (capInt ?? 0) >= 1);
  const canSubmit = !!title.trim() && capValid && effectiveCustomerId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New course</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="crs-title">Title</Label>
            <Input
              id="crs-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Introduction to Shakespeare"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="crs-desc">Description (optional)</Label>
            <Textarea
              id="crs-desc" rows={3}
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What callers will learn"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="crs-price">Price (USD)</Label>
              <Input
                id="crs-price" type="number" min="0" step="0.01"
                value={price} onChange={(e) => setPrice(e.target.value)}
                className="font-mono"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="crs-cap">Max enrollment (optional)</Label>
              <Input
                id="crs-cap" type="number" min="1" step="1"
                value={cap} onChange={(e) => setCap(e.target.value)}
                placeholder="unlimited"
                className="font-mono"
              />
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            disabled={create.isPending || !canSubmit}
            onClick={() => create.mutate({
              title: title.trim(),
              description: description.trim() || null,
              price_cents: priceCents,
              max_enrollment: capInt ?? null,
              ...(effectiveCustomerId
                ? { customer_id: effectiveCustomerId }
                : {}),
            })}
          >
            {create.isPending ? 'Creating…' : 'Create course'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
